import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

// --- Gap Zone Report (Phase 1) ---
export interface SeoReportKeyword {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface SeoReport {
  generatedAt: string;
  dateRange: { startDate: string; endDate: string };
  summary: {
    totalImpressions: number;
    totalClicks: number;
    avgPosition: number;
    gapZoneCount: number;
    totalKeywords: number;
  };
  gapZoneKeywords: SeoReportKeyword[];
}

export interface SeoNoReport {
  status: 'no_report';
  message: string;
}

export type SeoReportResponse = SeoReport | SeoNoReport;

// --- Competitor Report (Phase 2) ---
export interface CompetitorEntry {
  domain: string;
  url: string;
  title: string;
  estimatedPosition: number;
  contentType: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  whyWinning: string;
  specificFix: string;
}

export interface PlatformIntentKeyword {
  query: string;
  position: number;
  impressions: number;
  clicks: number;
  ctr: number;
  intentConfidence: 'high' | 'medium';
  competitors: CompetitorEntry[];
}

export interface CompetitorReport {
  generatedAt: string;
  platformIntentKeywords: PlatformIntentKeyword[];
  summary: {
    totalPlatformIntentKeywords: number;
    highThreatDomains: string[];
    mediumThreatDomains: string[];
    topOpportunity: string;
  };
}

export type CompetitorReportResponse = CompetitorReport | SeoNoReport;

// --- Content Plan (Phase 2) ---
export interface ContentPlanItem {
  title: string;
  targetKeyword: string;
  priority: 'P1' | 'P2';
  searchIntent: string;
  brief: string;
  estimatedImpressions: number;
  currentPosition: number;
  competitors: string[];
}

export interface ContentPlan {
  generatedAt: string;
  weekOf: string;
  items: ContentPlanItem[];
  summary: {
    p1Count: number;
    p2Count: number;
    totalEstimatedImpressions: number;
  };
}

export type ContentPlanResponse = ContentPlan | SeoNoReport;

// --- Full Report (all 3 combined) ---
export interface FullReportResult {
  seoReport: SeoReport;
  competitorReport: CompetitorReport;
  contentPlan: ContentPlan;
}

export const adminSeoApi = createApi({
  reducerPath: 'adminSeoApi',
  baseQuery: baseQuery,
  tagTypes: ['SeoReport', 'CompetitorReport', 'ContentPlan'],
  endpoints: (builder) => ({
    getSeoReport: builder.query<SeoReportResponse, void>({
      query: () => ({
        url: '/admin/seo/report',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['SeoReport'],
    }),
    generateSeoReport: builder.mutation<SeoReport, void>({
      query: () => ({
        url: '/admin/seo/generate',
        method: 'POST',
        requiresAuth: true,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminSeoApi.util.updateQueryData('getSeoReport', undefined, () => data)
          );
        } catch {
          // Error handled by the component
        }
      },
    }),
    getCompetitorReport: builder.query<CompetitorReportResponse, void>({
      query: () => ({
        url: '/admin/seo/competitor-report',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['CompetitorReport'],
    }),
    getContentPlan: builder.query<ContentPlanResponse, void>({
      query: () => ({
        url: '/admin/seo/content-plan',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['ContentPlan'],
    }),
    generateFullReport: builder.mutation<FullReportResult, void>({
      query: () => ({
        url: '/admin/seo/generate-full',
        method: 'POST',
        requiresAuth: true,
        timeout: 120000,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminSeoApi.util.updateQueryData('getSeoReport', undefined, () => data.seoReport)
          );
          dispatch(
            adminSeoApi.util.updateQueryData('getCompetitorReport', undefined, () => data.competitorReport)
          );
          dispatch(
            adminSeoApi.util.updateQueryData('getContentPlan', undefined, () => data.contentPlan)
          );
        } catch {
          // Error handled by the component
        }
      },
    }),
  }),
});

export const {
  useGetSeoReportQuery,
  useGenerateSeoReportMutation,
  useGetCompetitorReportQuery,
  useGetContentPlanQuery,
  useGenerateFullReportMutation,
} = adminSeoApi;
